/**
 * AddSongToPlaylistDialog — Modal "Añadir canción a Lista Personalizada"
 *
 * Extraído de App.js líneas ~8575-8823 durante la Ola A del refactor 2026-04-20.
 * Sin lógica propia nueva — encapsula el JSX + filtros existentes.
 *
 * El estado (searchText, filtros, isOpen) y el handler handleAddSongToCustomList
 * viven todavía en App.js y se inyectan por props para no alterar comportamiento.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Music, Heart, Star, Check, Plus, Loader2, EyeOff, Activity, ArrowUp, ArrowDown, ArrowRight } from 'lucide-react';
import { useSongs } from '../hooks/useSongs';
import axios from 'axios';
import { API_URL } from '../config';

export const AddSongToPlaylistDialog = ({
  isOpen,
  onClose,
  songs,
  currentPlaylist,
  availableGenres,
  searchText,
  setSearchText,
  searchGenre,
  setSearchGenre,
  addDialogFilterRating,
  setAddDialogFilterRating,
  addDialogShowFavoritesOnly,
  setAddDialogShowFavoritesOnly,
  onAddSong,
}) => {
  // Nuevos filtros feb/2026:
  //  - hideAlreadyInList: oculta canciones ya añadidas a la playlist actual
  //  - lastAddedSongId: guarda la última canción añadida en esta sesión del
  //    diálogo para calcular "BPM compatible ±5" con la siguiente
  const [hideAlreadyInList, setHideAlreadyInList] = useState(false);
  const [lastAddedSongId, setLastAddedSongId] = useState(null);

  // ============================================================
  // SERVER-SIDE SEARCH (feb/2026)
  // ------------------------------------------------------------
  // Antes: cargábamos la biblioteca COMPLETA (8702 canciones en prod) y
  // filtrábamos en cliente. Eso implicaba transferir ~5-10MB por 4G y
  // renderizar muchas filas invisibles.
  // Ahora: usamos /api/songs con filtros server-side + debounce 300ms.
  // hideAlreadyInList sigue siendo client-side porque es un concepto
  // específico de la playlist actual (el backend no lo conoce).
  // ============================================================
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchText || ''), 300);
    return () => clearTimeout(handler);
  }, [searchText]);

  // Filtros enviados al backend
  const backendFilters = useMemo(() => ({
    search: debouncedSearch,
    genre: searchGenre || undefined,
    rating_min: addDialogFilterRating || undefined,
    favorito: addDialogShowFavoritesOnly ? true : undefined,
    sortBy: 'rating',
    sortOrder: 'desc',
  }), [debouncedSearch, searchGenre, addDialogFilterRating, addDialogShowFavoritesOnly]);

  // Llamada paginada (hasta 100 resultados por lote) — suficiente porque
  // ya ordenamos por rating DESC y el usuario refinará con más filtros.
  const { data: pageData, isFetching: isFetchingFiltered } = useSongs(
    1,
    100,
    backendFilters,
    isOpen,
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intermediate value (optimisation)
  const filteredRaw = pageData?.data || [];
  const totalFilteredBackend = pageData?.pagination?.total || 0;

  // Total de biblioteca (cheap stats) — solo para el mensaje "de N en biblioteca"
  const [totalLibrary, setTotalLibrary] = useState(null);
  useEffect(() => {
    if (!isOpen || totalLibrary !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${API_URL}/songs?limit=1&page=1`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setTotalLibrary(data?.pagination?.total ?? 0);
      } catch {
        if (!cancelled) setTotalLibrary(0);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, totalLibrary]);

  // BPM de referencia: última canción añadida en esta sesión, o la última
  // canción de la playlist actual CON bpm>0.
  // Importante: para buscar el BPM de la playlist actual, podríamos no tener
  // esa canción en filteredRaw si el usuario filtró. Por eso aceptamos también
  // el contexto paginado (songs) como fuente de búsqueda.
  const referenceBpm = useMemo(() => {
    const ids = currentPlaylist?.lista_personalizada_generada || [];
    const searchPool = [...filteredRaw, ...(Array.isArray(songs) ? songs : [])];
    const findById = (id) => searchPool.find(x => x.id === id);
    // Prioridad 1: la última canción añadida en esta sesión del diálogo
    if (lastAddedSongId) {
      const s = findById(lastAddedSongId);
      if (s?.bpm) return { bpm: s.bpm, source: 'recent' };
    }
    // Prioridad 2: la última canción de la playlist actual con bpm válido
    for (let i = ids.length - 1; i >= 0; i--) {
      const s = findById(ids[i]);
      if (s?.bpm) return { bpm: s.bpm, source: 'playlist' };
    }
    return null;
  }, [lastAddedSongId, currentPlaylist, filteredRaw, songs]);

  // Reset de filtros al cerrar
  const handleOpenChange = (open) => {
    if (!open) {
      setSearchText('');
      setSearchGenre('');
      setAddDialogFilterRating('');
      setAddDialogShowFavoritesOnly(false);
      setHideAlreadyInList(false);
      setLastAddedSongId(null);
      onClose();
    }
  };

  // Set de IDs ya añadidos a la playlist actual (para filtrado O(1))
  const alreadyInListSet = useMemo(() => {
    return new Set(currentPlaylist?.lista_personalizada_generada || []);
  }, [currentPlaylist]);

  // Lógica de filtrado: el backend ya aplicó search/genre/rating/favorito.
  // Aquí sólo aplicamos hideAlreadyInList (client-side) y el sort personalizado
  // (favoritos primero, luego rating DESC, luego título alfabético).
  const filterAndSort = () => {
    let filtered = filteredRaw;
    if (hideAlreadyInList) {
      filtered = filtered.filter(song => !alreadyInListSet.has(song.id));
    }
    filtered = [...filtered].sort((a, b) => {
      if (a.favorito && !b.favorito) return -1;
      if (!a.favorito && b.favorito) return 1;
      const ratingA = a.rating || 0;
      const ratingB = b.rating || 0;
      if (ratingA !== ratingB) return ratingB - ratingA;
      return (a.title || '').localeCompare(b.title || '');
    });
    return filtered;
  };

  const hasAnyFilter =
    searchText || searchGenre || addDialogFilterRating || addDialogShowFavoritesOnly || hideAlreadyInList;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="bg-slate-800 border-slate-700 text-white w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6"
        data-testid="add-song-to-playlist-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl text-white">
            Añadir a Lista
          </DialogTitle>
          <p className="text-slate-400 text-xs sm:text-sm">
            Selecciona canciones de tu biblioteca
          </p>
        </DialogHeader>
        <div className="space-y-3 sm:space-y-4 min-w-0">
          {/* Búsqueda rápida */}
          <div className="flex flex-col sm:grid sm:grid-cols-2 gap-2 sm:gap-3">
            <Input
              type="text"
              placeholder="Buscar título o artista..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="bg-slate-900 border-slate-700 text-white text-sm"
              data-testid="add-dialog-search-input"
            />
            <select
              value={searchGenre}
              onChange={(e) => setSearchGenre(e.target.value)}
              className="bg-slate-900 border border-slate-700 text-white rounded-md px-2 py-2 text-sm"
              data-testid="add-dialog-genre-select"
            >
              <option value="">Todos los estilos</option>
              {availableGenres.map((genre) => (
                <option key={genre} value={genre}>
                  {genre}
                </option>
              ))}
            </select>
          </div>

          {/* Filtros adicionales */}
          <div className="flex flex-wrap gap-2 items-center">
            <select
              value={addDialogFilterRating}
              onChange={(e) => setAddDialogFilterRating(e.target.value)}
              className="bg-slate-900 border border-slate-700 text-white rounded-md px-2 py-1.5 text-xs sm:text-sm"
              data-testid="add-dialog-rating-select"
            >
              <option value="">Rating</option>
              <option value="5">5★</option>
              <option value="4">4+★</option>
              <option value="3">3+★</option>
            </select>

            <Button
              variant={addDialogShowFavoritesOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setAddDialogShowFavoritesOnly(!addDialogShowFavoritesOnly)}
              className={`text-xs ${
                addDialogShowFavoritesOnly
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'border-slate-600 text-slate-300 hover:bg-slate-700'
              }`}
              data-testid="add-dialog-favorites-toggle"
            >
              <Heart
                className={`w-3 h-3 sm:w-4 sm:h-4 sm:mr-1 ${
                  addDialogShowFavoritesOnly ? 'fill-current' : ''
                }`}
              />
              <span className="hidden sm:inline">Favoritos</span>
            </Button>

            <Button
              variant={hideAlreadyInList ? 'default' : 'outline'}
              size="sm"
              onClick={() => setHideAlreadyInList(!hideAlreadyInList)}
              className={`text-xs ${
                hideAlreadyInList
                  ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                  : 'border-slate-600 text-slate-300 hover:bg-slate-700'
              }`}
              data-testid="add-dialog-hide-already-toggle"
              title="Ocultar canciones que ya están en esta playlist"
            >
              <EyeOff className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
              <span className="hidden sm:inline">Ocultar ya añadidas</span>
              <span className="sm:hidden">Nuevas</span>
            </Button>

            {hasAnyFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchText('');
                  setSearchGenre('');
                  setAddDialogFilterRating('');
                  setAddDialogShowFavoritesOnly(false);
                  setHideAlreadyInList(false);
                }}
                className="text-slate-400 hover:text-white text-xs"
                data-testid="add-dialog-clear-filters"
              >
                Limpiar
              </Button>
            )}
          </div>

          {/* BPM de referencia (para recomendaciones de compatibilidad) */}
          {referenceBpm && (
            <div
              className="flex items-center gap-2 text-xs text-slate-400 bg-slate-900/60 border border-slate-700 rounded-md px-3 py-2"
              data-testid="add-dialog-reference-bpm"
            >
              <Activity className="w-4 h-4 text-green-400 shrink-0" />
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>
                  BPM de referencia:{' '}
                  <span className="text-green-300 font-mono font-semibold">{referenceBpm.bpm}</span>
                  {' '}
                  <span className="text-slate-500">
                    ({referenceBpm.source === 'recent'
                      ? 'última que has añadido'
                      : 'última de la playlist'})
                  </span>
                </span>
                <span className="text-slate-500 flex items-center gap-1">
                  · <ArrowUp className="w-3 h-3 text-orange-400" /> sube ·
                  <ArrowDown className="w-3 h-3 text-blue-400" /> baja ·
                  <span className="text-green-300 font-semibold ml-1">±5 BPM</span> compatible
                </span>
              </span>
            </div>
          )}

          {/* Lista de canciones */}
          <div className="max-h-96 overflow-y-auto space-y-2">
            {isFetchingFiltered && filteredRaw.length === 0 ? (
              <div className="text-center py-8 text-slate-400" data-testid="add-dialog-loading-library">
                <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-cyan-400" />
                <p className="text-sm">Buscando en la biblioteca...</p>
              </div>
            ) : (() => {
              const filtered = filterAndSort();
              return filtered.slice(0, 50).map((song) => {
                const isInList =
                  currentPlaylist?.lista_personalizada_generada?.includes(song.id);
                return (
                  <div
                    key={song.id}
                    className={`p-3 rounded-lg flex items-center justify-between gap-2 min-w-0 ${
                      isInList
                        ? 'bg-green-900/30 border border-green-600/50'
                        : 'bg-slate-900/50 hover:bg-slate-900'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium text-sm truncate">
                          {song.title}
                        </p>
                        {song.favorito && (
                          <Heart className="w-4 h-4 text-red-500 fill-red-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-slate-400 text-xs truncate">{song.artist}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {song.rating > 0 && (
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className={`w-3 h-3 ${
                                  star <= song.rating
                                    ? 'text-yellow-400 fill-yellow-400'
                                    : 'text-slate-600'
                                }`}
                              />
                            ))}
                          </div>
                        )}
                        {song.genres && song.genres.length > 0 && (
                          <>
                            {song.genres.slice(0, 2).map((genre) => (
                              <span
                                key={genre}
                                className="px-2 py-0.5 bg-cyan-600/30 text-cyan-300 rounded text-xs"
                              >
                                {genre}
                              </span>
                            ))}
                          </>
                        )}
                        {song.bpm && (() => {
                          const refBpm = referenceBpm?.bpm;
                          const rawDiff = refBpm ? song.bpm - refBpm : null;
                          const absDiff = rawDiff !== null ? Math.abs(rawDiff) : null;
                          const isCompatible = refBpm && absDiff !== null && absDiff <= 5;
                          // Dirección energética respecto a la referencia:
                          //   rawDiff > 0 → sube energía (↑)
                          //   rawDiff < 0 → baja energía (↓)
                          //   rawDiff = 0 → mismo tempo (→)
                          const dir =
                            rawDiff === null || rawDiff === 0 ? 'same' :
                            rawDiff > 0 ? 'up' : 'down';
                          const DirIcon = dir === 'up' ? ArrowUp : dir === 'down' ? ArrowDown : ArrowRight;
                          const dirColor =
                            dir === 'up' ? 'text-orange-400' :
                            dir === 'down' ? 'text-blue-400' :
                            'text-slate-500';
                          const dirLabel =
                            dir === 'up' ? `sube ${rawDiff} BPM (más energía)` :
                            dir === 'down' ? `baja ${Math.abs(rawDiff)} BPM (menos energía)` :
                            'mismo tempo';
                          return (
                            <>
                              <span className={`text-xs flex items-center gap-0.5 ${isCompatible ? 'text-green-300 font-semibold' : 'text-slate-500'}`}>
                                {song.bpm} BPM
                                {refBpm && (
                                  <DirIcon
                                    className={`w-3 h-3 ${dirColor}`}
                                    data-testid={`bpm-dir-${dir}-${song.id}`}
                                  />
                                )}
                              </span>
                              {isCompatible && (
                                <span
                                  className="px-2 py-0.5 bg-green-600/25 border border-green-500/50 text-green-300 rounded text-xs font-medium flex items-center gap-1"
                                  data-testid={`bpm-compatible-badge-${song.id}`}
                                  title={`Diferencia de ${absDiff} BPM con la canción de referencia (${refBpm} BPM) — ${dirLabel}`}
                                >
                                  <Activity className="w-3 h-3" />
                                  BPM compatible
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!isInList) {
                          onAddSong(song.id);
                          setLastAddedSongId(song.id);
                        }
                      }}
                      disabled={isInList}
                      className={`shrink-0 ${isInList ? 'bg-green-600' : 'bg-cyan-600 hover:bg-cyan-700'}`}
                      data-testid={`add-dialog-add-btn-${song.id}`}
                    >
                      {isInList ? (
                        <>
                          <Check className="w-4 h-4 mr-1" />
                          En lista
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-1" />
                          Añadir
                        </>
                      )}
                    </Button>
                  </div>
                );
              });
            })()}
          </div>

          {(() => {
            if (isFetchingFiltered && filteredRaw.length === 0) {
              return null;
            }
            const filtered = filterAndSort();
            const filteredLen = filtered.length;
            const libCount = totalLibrary ?? totalFilteredBackend;
            const hiddenByList = filteredRaw.length - filteredLen;
            if (filteredLen === 0) {
              return (
                <div className="text-center py-8 text-slate-400">
                  <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No se encontraron canciones</p>
                  {hasAnyFilter && (
                    <p className="text-sm mt-2">
                      Intenta con otros criterios de búsqueda
                    </p>
                  )}
                </div>
              );
            }
            // Total real del backend tras filtros (puede ser >50 si hay muchos matches)
            const backendTotal = totalFilteredBackend;
            return (
              <p className="text-slate-400 text-sm text-center" data-testid="add-dialog-results-count">
                {backendTotal > filteredRaw.length ? (
                  <>
                    Mostrando 50 de {backendTotal} canciones{' '}
                    {libCount ? `(biblioteca: ${libCount})` : ''} — refina con más filtros.
                  </>
                ) : (
                  <>
                    {filteredLen}{' '}
                    {filteredLen === 1 ? 'canción encontrada' : 'canciones encontradas'}
                    {libCount && libCount > filteredLen ? ` (de ${libCount} en biblioteca)` : ''}
                    {hiddenByList > 0 ? ` · ${hiddenByList} ocultas ya en la lista` : ''}
                  </>
                )}
                {' '}(ordenadas por favoritos y rating)
              </p>
            );
          })()}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button
            onClick={() => handleOpenChange(false)}
            variant="outline"
            className="border-slate-600 text-white hover:bg-slate-700"
            data-testid="add-dialog-close-btn"
          >
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddSongToPlaylistDialog;
